import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUpIcon, ZapIcon, GlobeIcon, HandshakeIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type WhyChamaleonProps = {
  language: Language;
};
const values = {
  ua: [{
    icon: TrendingUpIcon,
    title: 'Безперервно покращуємо сервіс',
    description: 'Постійний розвиток та вдосконалення процесів для найкращого досвіду клієнтів'
  }, {
    icon: ZapIcon,
    title: 'Сучасний підхід і швидкі рішення',
    description: 'Інноваційні технології та оперативне реагування на потреби ринку'
  }, {
    icon: GlobeIcon,
    title: 'Міжнародна команда',
    description: "Експерти з різних країн об'єднані спільною метою якості"
  }, {
    icon: HandshakeIcon,
    title: 'Партнерська підтримка B2B',
    description: 'Комплексна підтримка бізнес-партнерів на всіх етапах співпраці'
  }],
  en: [{
    icon: TrendingUpIcon,
    title: 'Continuously improving service',
    description: 'Constant development and process improvement for the best customer experience'
  }, {
    icon: ZapIcon,
    title: 'Modern approach & fast solutions',
    description: 'Innovative technologies and prompt response to market needs'
  }, {
    icon: GlobeIcon,
    title: 'International team',
    description: 'Experts from different countries united by a common goal of quality'
  }, {
    icon: HandshakeIcon,
    title: 'B2B partner support',
    description: 'Comprehensive support for business partners at all stages of cooperation'
  }]
};
const sectionText = {
  ua: {
    title: 'Чому CHAMALEON?'
  },
  en: {
    title: 'Why CHAMALEON?'
  }
};
export function WhyChamaleon({
  language
}: WhyChamaleonProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const valueList = values[language];
  return <section ref={ref} className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-303">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-304">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} data-id="element-305" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/2 left-0 w-px h-32 bg-gradient-to-b from-transparent via-chamaleon-yellow/50 to-transparent" data-id="element-306" />
      <div className="absolute top-1/2 right-0 w-px h-32 bg-gradient-to-b from-transparent via-chamaleon-yellow/50 to-transparent" data-id="element-307" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-308">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16" data-id="element-309">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4" data-id="element-310">
            {t.title}
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto" data-id="element-311" />
        </motion.div>

        {/* Values Grid - Asymmetric Glass Cards (Bento) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 relative z-10" data-id="element-312">
          {valueList.map((value, index) => {
          const Icon = value.icon;
          // Asymmetric pattern: Wide, Square, Square, Wide
          const isWide = index === 0 || index === 3;
          return <motion.div key={value.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className={`group h-full ${isWide ? 'md:col-span-2' : 'md:col-span-1'}`} data-id="element-313">
                <div className="relative overflow-hidden rounded-3xl bg-chamaleon-yellow p-8 sm:p-10 h-full flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(255,184,28,0.3)]" data-id="element-314">
                  {/* Large Background Number */}
                  <div className="absolute -bottom-4 -right-4 text-[12rem] font-black text-chamaleon-black/5 leading-none select-none pointer-events-none group-hover:text-chamaleon-black/10 group-hover:-translate-y-4 group-hover:-translate-x-4 transition-all duration-700" data-id="element-315">
                    0{index + 1}
                  </div>

                  {/* Top Section: Icon */}
                  <div className="relative z-10 flex justify-between items-start mb-12" data-id="element-316">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-chamaleon-black/5 border border-chamaleon-black/10 group-hover:bg-chamaleon-black/10 transition-all duration-500" data-id="element-317">
                      <Icon className="w-7 h-7 text-chamaleon-black" data-id="element-318" />
                    </div>
                  </div>

                  {/* Bottom Section: Content */}
                  <div className="relative z-10 max-w-xl" data-id="element-319">
                    <h3 className={`font-bold text-chamaleon-black mb-3 ${isWide ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`} data-id="element-320">
                      {value.title}
                    </h3>
                    <p className="text-chamaleon-black/70 font-medium leading-relaxed text-sm sm:text-base" data-id="element-321">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}